/**
 * 计时器通知服务
 * 提供铃声播放和震动提醒功能，支持多平台兼容
 */

import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

// 音频循环播放实例
let audioLoop: HTMLAudioElement | null = null;
let vibrationInterval: NodeJS.Timeout | null = null;
let isLooping = false;

// 试听音频实例
let testAudio: HTMLAudioElement | null = null;
let isTestingSound = false;

/**
 * 开始循环播放计时器提示音
 */
export const startLoopingSound = async (soundFile: 'vivora' | 'delay' | 'sms'): Promise<void> => {
  try {
    // 如果已经在循环播放，先停止
    if (isLooping) {
      stopLoopingSound();
    }

    // 创建新的音频实例
    audioLoop = new Audio(`/audio/${soundFile}.mp3`);
    audioLoop.loop = true;
    audioLoop.preload = 'auto';

    // 开始播放
    const playPromise = audioLoop.play();

    if (playPromise !== undefined) {
      await playPromise;
      isLooping = true;
      console.log(`Timer sound looping started: ${soundFile}.mp3`);
    }

  } catch (error) {
    console.error('Failed to start looping timer sound:', error);
    isLooping = false;
  }
};

/**
 * 停止循环播放计时器提示音
 */
export const stopLoopingSound = (): void => {
  try {
    if (audioLoop) {
      audioLoop.pause();
      audioLoop.currentTime = 0;
      audioLoop = null;
    }
    isLooping = false;
    console.log('Timer sound looping stopped');
  } catch (error) {
    console.error('Failed to stop looping timer sound:', error);
  }
};

/**
 * 开始持续震动提醒
 */
export const startContinuousVibration = async (): Promise<void> => {
  try {
    // 如果已经在震动，先停止
    if (vibrationInterval) {
      stopContinuousVibration();
    }

    // 优先使用 Capacitor Haptics API
    if (Capacitor.isNativePlatform()) {
      // 立即震动一次
      await Haptics.impact({ style: ImpactStyle.Medium });

      // 每2秒震动一次
      vibrationInterval = setInterval(async () => {
        try {
          await Haptics.impact({ style: ImpactStyle.Medium });
        } catch (error) {
          console.error('Failed to trigger haptic feedback:', error);
        }
      }, 2000);

      console.log('Continuous haptic feedback started (Capacitor)');
    } else {
      // 降级到浏览器 vibrate API
      if ('vibrate' in navigator) {
        // 立即震动一次
        navigator.vibrate(200);

        // 每2秒震动一次，每次200ms
        vibrationInterval = setInterval(() => {
          if ('vibrate' in navigator) {
            navigator.vibrate(200);
          }
        }, 2000);

        console.log('Continuous vibration started (Web API)');
      } else {
        console.warn('Vibration not supported on this device');
      }
    }

  } catch (error) {
    console.error('Failed to start continuous vibration:', error);
  }
};

/**
 * 停止持续震动提醒
 */
export const stopContinuousVibration = (): void => {
  try {
    if (vibrationInterval) {
      clearInterval(vibrationInterval);
      vibrationInterval = null;
    }

    // 对于浏览器环境，停止当前震动
    if (!Capacitor.isNativePlatform() && 'vibrate' in navigator) {
      navigator.vibrate(0);
    }

    console.log('Continuous vibration stopped');
  } catch (error) {
    console.error('Failed to stop continuous vibration:', error);
  }
};

/**
 * 测试震动功能（单次震动）
 */
export const testVibration = async (): Promise<void> => {
  try {
    // 优先使用 Capacitor Haptics API
    if (Capacitor.isNativePlatform()) {
      await Haptics.impact({ style: ImpactStyle.Medium });
      console.log('Test haptic feedback triggered (Capacitor)');
    } else {
      // 降级到浏览器 vibrate API
      if ('vibrate' in navigator) {
        navigator.vibrate(200);
        console.log('Test vibration triggered (Web API)');
      } else {
        console.warn('Vibration not supported on this device');
      }
    }
  } catch (error) {
    console.error('Failed to test vibration:', error);
  }
};

/**
 * 播放计时器提示音一次（用于试听）
 */
export const playTimerSoundOnce = async (soundFile: 'vivora' | 'delay' | 'sms'): Promise<void> => {
  try {
    // 创建临时音频实例
    const audio = new Audio(`/audio/${soundFile}.mp3`);
    audio.preload = 'auto';

    // 播放音频
    const playPromise = audio.play();

    if (playPromise !== undefined) {
      await playPromise;
      console.log(`Timer sound played once: ${soundFile}.mp3`);
    }

  } catch (error) {
    console.error('Failed to play timer sound once:', error);

    // 降级方案：系统提示音（如果支持）
    try {
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance('计时结束');
        utterance.volume = 0.1;
        utterance.rate = 2;
        speechSynthesis.speak(utterance);
      }
    } catch (speechError) {
      console.error('Speech synthesis also failed:', speechError);
    }
  }
};

/**
 * 开始试听计时器提示音（循环播放）
 */
export const startTestSound = async (soundFile: 'vivora' | 'delay' | 'sms'): Promise<void> => {
  try {
    // 如果已经在试听，先停止
    if (isTestingSound) {
      stopTestSound();
    }

    // 创建新的音频实例
    testAudio = new Audio(`/audio/${soundFile}.mp3`);
    testAudio.loop = true;
    testAudio.preload = 'auto';

    // 开始播放
    const playPromise = testAudio.play();

    if (playPromise !== undefined) {
      await playPromise;
      isTestingSound = true;
      console.log(`Test sound looping started: ${soundFile}.mp3`);
    }

  } catch (error) {
    console.error('Failed to start test sound:', error);
    isTestingSound = false;
  }
};

/**
 * 停止试听计时器提示音
 */
export const stopTestSound = (): void => {
  try {
    if (testAudio) {
      testAudio.pause();
      testAudio.currentTime = 0;
      testAudio = null;
    }
    isTestingSound = false;
    console.log('Test sound stopped');
  } catch (error) {
    console.error('Failed to stop test sound:', error);
  }
};

/**
 * 检查是否正在试听音频
 */
export const isTestingSoundActive = (): boolean => {
  return isTestingSound;
};

/**
 * 停止所有计时器通知
 */
export const stopAllTimerNotifications = (): void => {
  stopLoopingSound();
  stopContinuousVibration();
  stopTestSound();
};
